import { useEffect, useState } from "react";
import { api, apiError, eur } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil } from "lucide-react";

const EMPTY = { key: "", name: "", price_monthly: "", lookup_key: "", users: 1, storage_mb: 1024, ki_imports: 10, features: "", enterprise: false };

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEdit(p);
    setForm({ key: p.key, name: p.name, price_monthly: p.price_monthly ?? "", lookup_key: p.lookup_key || "",
      users: p.limits?.users ?? 1, storage_mb: p.limits?.storage_mb ?? 1024, ki_imports: p.limits?.ki_imports ?? 10,
      features: (p.features || []).join("\n"), enterprise: !!p.enterprise });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      key: form.key, name: form.name, enterprise: form.enterprise,
      price_monthly: form.price_monthly === "" || form.price_monthly == null ? null : parseFloat(form.price_monthly),
      lookup_key: form.lookup_key || null,
      limits: { users: parseInt(form.users), storage_mb: parseInt(form.storage_mb), ki_imports: parseInt(form.ki_imports) },
      features: form.features.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (edit) await api.put(`/admin/plans/${edit.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success("Tarif gespeichert.");
      setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Plattform</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Tarifverwaltung</h1>
          <p className="text-sm text-zinc-500 mt-1">Preise und Leistungsgrenzen frei konfigurieren. Limits greifen sofort pro Mandant.</p>
        </div>
        <Button onClick={() => { setEdit(null); setForm(EMPTY); setOpen(true); }} className="bg-zinc-900 text-white hover:bg-zinc-800" data-testid="new-plan-button"><Plus className="w-4 h-4 mr-1" /> Neuer Tarif</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm" data-testid={`admin-plan-${p.key}`}>
            <div className="flex justify-between items-start">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">{p.key}</div>
              <button onClick={() => openEdit(p)} className="text-zinc-400 hover:text-zinc-900" data-testid={`edit-plan-${p.key}`}><Pencil className="w-4 h-4" /></button>
            </div>
            <div className="font-display text-xl font-semibold mt-2">{p.name}</div>
            <div className="text-zinc-400 mt-1">{p.price_monthly != null ? `${eur(p.price_monthly)}/Monat` : "Individuell"}</div>
            <div className="mt-4 space-y-1.5 text-xs font-mono text-zinc-500">
              <div>Benutzer: {p.limits?.users === -1 ? "∞" : p.limits?.users}</div>
              <div>Speicher: {p.limits?.storage_mb === -1 ? "∞" : `${p.limits?.storage_mb} MB`}</div>
              <div>KI-Imports: {p.limits?.ki_imports === -1 ? "∞" : p.limits?.ki_imports}/Monat</div>
              <div>Stripe: {p.lookup_key || "–"}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="plan-form-dialog">
          <DialogHeader><DialogTitle>{edit ? "Tarif bearbeiten" : "Neuer Tarif"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Schlüssel</Label><Input value={form.key} disabled={!!edit} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") })} data-testid="plan-key" className="mt-1.5 font-mono" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" className="mt-1.5" /></div>
            <div><Label>Preis €/Monat (leer = individuell)</Label><Input type="number" step="0.01" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} data-testid="plan-price" className="mt-1.5" /></div>
            <div><Label>Stripe Lookup-Key</Label><Input value={form.lookup_key} onChange={(e) => setForm({ ...form, lookup_key: e.target.value })} placeholder="z. B. pro_monthly" className="mt-1.5 font-mono" /></div>
            <div><Label>Benutzer (-1 = ∞)</Label><Input type="number" value={form.users} onChange={(e) => setForm({ ...form, users: e.target.value })} data-testid="plan-users" className="mt-1.5" /></div>
            <div><Label>Speicher MB (-1 = ∞)</Label><Input type="number" value={form.storage_mb} onChange={(e) => setForm({ ...form, storage_mb: e.target.value })} data-testid="plan-storage" className="mt-1.5" /></div>
            <div><Label>KI-Imports/Monat (-1 = ∞)</Label><Input type="number" value={form.ki_imports} onChange={(e) => setForm({ ...form, ki_imports: e.target.value })} data-testid="plan-ki" className="mt-1.5" /></div>
            <div className="flex items-end pb-2 gap-2">
              <input type="checkbox" id="ent" checked={form.enterprise} onChange={(e) => setForm({ ...form, enterprise: e.target.checked })} className="w-4 h-4 accent-zinc-900" />
              <Label htmlFor="ent">Enterprise (Anfrage)</Label>
            </div>
            <div className="col-span-2"><Label>Leistungen (eine pro Zeile)</Label>
              <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={4} data-testid="plan-features"
                className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <Button onClick={save} disabled={!form.key || !form.name} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white mt-4" data-testid="plan-save-button">Speichern</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
